"use client";

import type React from "react";
import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  X,
  Heart,
  Share2,
  Eye,
  Download,
  MessageCircle,
  Star,
  Play,
  Clock,
  Calendar,
  Tag,
  TrendingUp,
  Send,
  CheckCircle,
  FolderPlus,
  Edit,
} from "lucide-react";
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import dreamWorldProvider from "../DreamWorldProvider";
import { Comment, LibraryItem } from "../types/library";
import AddToAlbumModal from "./AddToAlbumModal";
import { Effect, Style } from "../types/effects";
import LoginPromptModal from "./modals/LoginPromptModal";
import CreationReadyModal from "./CreationReadyModal";
import EffectDescriptionPanel from "./create/EffectDescriptionPanel";
import PublishContent from "./create/PublishContent";
import { Notification } from "../types/notifications";
import { EditingTurn, InteractiveEditingData } from "../types/library";
import uploadImageToFirebase from "../utils/uploadImageToFirebase";
import { Modality } from "@google/genai";

interface ContentModalProps {
  contentId: string;
  source: "discover" | "tag" | "library" | "profile" | "album" | "following";
  albumId?: string;
  onClose: () => void;
  onLike: (id: string) => void;
  initialContent?: LibraryItem;
}

const ContentModal: React.FC<ContentModalProps> = ({
  contentId,
  source,
  albumId,
  onClose,
  onLike,
  initialContent,
}) => {
  const contentFromStore = useSelector((state: RootState) => {
    if (initialContent) return null;
    if (source === "discover") {
      return state.discover.content.find((item) => item.id === contentId);
    } else if (source === "tag") {
      return state.tag.content.find((item) => item.id === contentId);
    } else if (source === "library") {
      return state.dreamworld.libraryContent.find(
        (item) => item.id === contentId
      );
    } else if (source === "profile") {
      return state.profile.content.find((item) => item.id === contentId);
    } else if (source === "album" && albumId) {
      return state.dreamworld.albumContent[albumId]?.find(
        (item) => item.id === contentId
      );
    } else if (source === "following") {
      return state.following.posts.find((item) => item.id === contentId);
    }
    return null;
  });

  const content = initialContent || contentFromStore;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAddToAlbumModal, setShowAddToAlbumModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [selectedEffect, setSelectedEffect] = useState<Effect | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoginPromptModalOpen, setIsLoginPromptModalOpen] = useState(false);
  const [isCreationReadyModalOpen, setIsCreationReadyModalOpen] =
    useState(false);
  const [newCreationId, setNewCreationId] = useState<string | null>(null);
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const navigate = useNavigate();

  const observer = useRef<IntersectionObserver | null>(null);
  const lastCommentElementRef = useCallback(
    (node: any) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && lastVisible) {
          fetchComments(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, lastVisible]
  );

  const fetchComments = async (loadMore = false) => {
    if (!content) return;
    setIsLoading(true);
    try {
      const { comments: newComments, lastVisible: newLastVisible } =
        await dreamWorldProvider.getComments(
          content.id,
          loadMore ? lastVisible : undefined
        );
      setComments(loadMore ? [...comments, ...newComments] : newComments);
      setLastVisible(newLastVisible);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (content) {
      fetchComments();
    }
  }, [content]);

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !content || !userGlobalData?.userData) return;

    setIsLoading(true);
    try {
      const creator = userGlobalData.userData;
      const addedComment = await dreamWorldProvider.addComment(
        content.id,
        newComment,
        creator
      );
      setComments([addedComment, ...comments]);
      setNewComment("");
      setShowSuccessModal(true);

      if (
        userGlobalData &&
        content.creator &&
        content.creator.uid !== userGlobalData.userData.uid
      ) {
        const notification: Omit<Notification, "id" | "timestamp"> = {
          recipientId: content.creator.uid,
          senderId: userGlobalData.userData.uid,
          type: "comment",
          contentId: content.id,
          read: false,
        };
        await dreamWorldProvider.createNotification(notification);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Safety check for content prop
  if (!content) {
    return null;
  }

  const requireAuth = (action: () => void) => {
    if (!userGlobalData) {
      setIsLoginPromptModalOpen(true);
    } else {
      action();
    }
  };

  const handleLike = () => {
    requireAuth(() => onLike(content.id));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: content.title,
        text: `Check out this amazing creation by ${content.creator?.username || "Unknown"}`,
        url: window.location.href,
      });
    }
  };

  const handleDownload = () => {
    requireAuth(() => {
      // Download functionality
      const link = document.createElement("a");
      link.href = content.fullUrl;
      link.download = `${content.title.replace(/\s+/g, "_")}.${content.type?.toLowerCase() === "video" ? "mp4" : "jpg"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePublishModal = () => {
    requireAuth(() => setShowPublishModal(true));
  };

  const handleStartEditingFromModal = async () => {
    if (!content) return;
    requireAuth(async () => {
      try {
      const docId = uuidv4();
      const newEditingData = {
        id: docId,
        ownerId: userGlobalData!.user.uid,
        title: content.title,
        originalImageUrls: [content.fullUrl],
        createdAt: Timestamp.now(),
        turnsCount: 1,
        editings: [
          {
            id: uuidv4(),
            imageUrl: [content.fullUrl],
            request: "Start editing",
            generatedImageUrl: content.fullUrl,
            date: Timestamp.now(),
            isSavedToLib: false,
            isPublished: false,
          },
        ],
      };
      await dreamWorldProvider.addInteractiveEditingData(newEditingData);
      navigate(`/interactive-editing/${docId}`);
    } catch (error) {
      console.error("Error starting interactive editing:", error);
    }
    });
  };

  const handleMediaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Allow media controls to work without closing modal
  };

  const handleStartEditing = async (
    effect: Effect,
    images: File[],
    userInput?: string,
    style?: Style
  ) => {
    if (!userGlobalData || images.length === 0) return;

    setIsEditing(true);

    try {
      const originalImageUrls = await Promise.all(
        images.map((image) => uploadImageToFirebase(image, "originalImages"))
      );

      const ai = dreamWorldProvider.getImageAI();

      const imageParts = await Promise.all(
        images.map(async (image) => {
          const fileAsBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = () =>
              resolve((reader.result as string).split(",")[1]);
            reader.onerror = (error) => reject(error);
          });
          return {
            inlineData: {
              mimeType: image.type,
              data: fileAsBase64,
            },
          };
        })
      );

      let promptText = userInput
        ? `${effect.prompt} User input: ${userInput}`
        : effect.prompt;

      if (style) {
        promptText = `${promptText} Style: ${style.name}`;
      }

      const contents = [{ text: promptText }, ...imageParts];

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-image-preview",
        contents: contents,
        config: {
          responseModalities: [Modality.TEXT, Modality.IMAGE],
        },
      });

      if (
        response.candidates &&
        response.candidates.length > 0 &&
        response.candidates[0].content &&
        response.candidates[0].content.parts
      ) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData && part.inlineData.data) {
            const imageData = part.inlineData.data;
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            const file = new File([blob], "edited_image.png", {
              type: "image/png",
            });
            const generatedImageUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );

            const docId = uuidv4();
            var newEditingData: InteractiveEditingData = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              title: effect.title,
              originalImageUrls: originalImageUrls,
              createdAt: Timestamp.now(),
              turnsCount: 1,
              effectId: effect.id,
              editings: [
                {
                  id: uuidv4(),
                  imageUrl: originalImageUrls,
                  request: promptText,
                  generatedImageUrl,
                  date: Timestamp.now(),
                  isSavedToLib: false,
                  isPublished: false,
                },
              ],
            };

            await dreamWorldProvider.addInteractiveEditingData(newEditingData);
            setNewCreationId(docId);
            setIsCreationReadyModalOpen(true);
            setSelectedEffect(null);
          }
        }
      }
    } catch (error) {
      console.error("Error starting interactive editing:", error);
    } finally {
      setIsEditing(false);
      setSelectedEffect(null);
    }
  };

  // Render media content (video or image)
  const renderMediaContent = () => {
    if (content.type?.toLowerCase() === "video" && content.fullUrl) {
      return (
        <video
          src={content.fullUrl}
          className="max-w-full max-h-full object-contain"
          controls
          preload="metadata"
          onClick={handleMediaClick}
          onMouseDown={(e) => e.stopPropagation()}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    return (
      <img
        src={
          content.fullUrl ||
          "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
        }
        alt={content.title}
        className="max-w-full max-h-full object-contain"
      />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="absolute right-0 top-0 h-full w-full md:relative md:inset-0 md:w-full md:max-w-6xl md:max-h-[90vh] md:mx-auto md:my-auto md:rounded-2xl bg-gray-900 md:shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        <div className="flex flex-col md:flex-row h-full overflow-y-auto md:overflow-hidden">
          {/* Media Section */}
          <div className="relative flex-1 md:flex-[2] bg-black flex items-center justify-center min-h-[250px] md:min-h-[500px]">
            {renderMediaContent()}

            {/* Play button overlay - only show for videos without fullUrl (fallback) */}
            {content.type?.toLowerCase() === "video" && !content.fullUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black/60 rounded-full p-6 hover:bg-black/80 transition-colors cursor-pointer">
                  <Play className="w-12 h-12 text-white fill-current" />
                </div>
              </div>
            )}

            {/* Duration for videos - only show if not an actual video element */}
            {content.duration && content.type?.toLowerCase() !== "video" && (
              <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-lg text-sm flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{content.duration}s</span>
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {content.isTrending && (
                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Trending</span>
                </span>
              )}
              <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                {content.type?.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="flex-1 md:flex-[1] md:overflow-y-auto">
            <div className="p-4 sm:p-6 md:p-8">
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-xl sm:text-2xl md:text-lg font-bold text-white mb-4 leading-tight line-clamp-3">
                  {content.title}
                </h1>

                {/* Creator info */}
                {content.creator && (
                  <div className="flex items-center space-x-3 mb-4">
                    <img
                      src={
                        content.creator.avatarUrl ||
                        "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                      }
                      alt={content.creator.username}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-2 ring-purple-500/50"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-white text-sm sm:text-base">
                          {content.creator.username}
                        </h3>
                        {true && (
                          <Star className="w-4 h-4 text-blue-400 fill-current" />
                        )}
                        {3 <= 3 && (
                          <span className="bg-yellow-500 text-black px-2 py-0.5 rounded-full text-xs font-bold">
                            #1
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-400">
                        {(content.creator.followers ?? 0).toLocaleString()}{" "}
                        followers
                      </p>
                    </div>
                    {userGlobalData?.userData?.uid !== content.creator.uid && (
                      <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        Follow
                      </button>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center space-x-6 text-sm text-gray-400 mb-4">
                  <div className="flex items-center space-x-2">
                    <Heart className="w-4 h-4" />
                    <span>{content.likes.toLocaleString()} likes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>{content.views.toLocaleString()} views</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(
                        (content.createdAt as any).seconds * 1000
                      ).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLike}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      content.isLiked
                        ? "bg-red-500 text-white"
                        : "bg-gray-800 text-gray-300 hover:bg-red-500 hover:text-white"
                    }`}
                  >
                    <Heart className="w-4 h-4" />
                    <span>Like</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleShare}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-blue-500 hover:text-white rounded-lg font-medium transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownload}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-green-500 hover:text-white rounded-lg font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </motion.button>

                  {content.type === "Image" && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleStartEditingFromModal}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-purple-500 hover:text-white rounded-lg font-medium transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit this image</span>
                    </motion.button>
                  )}

                  {userGlobalData?.userData?.uid === content.creator?.uid && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddToAlbumModal(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-yellow-500 hover:text-white rounded-lg font-medium transition-colors"
                    >
                      <FolderPlus className="w-4 h-4" />
                      <span>Add to Album</span>
                    </motion.button>
                  )}

                  {!content.isPublished && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handlePublishModal}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-blue-500 hover:text-white rounded-lg font-medium transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      <span>Publish Content</span>
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Prompt */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                  <MessageCircle className="w-5 h-5" />
                  <span>Prompt</span>
                </h3>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <p
                    className={`text-gray-300 text-sm sm:text-base leading-relaxed ${!isPromptExpanded ? "line-clamp-3" : ""}`}
                  >
                    {content.prompt}
                  </p>
                  <button
                    onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                    className="text-purple-400 hover:text-purple-300 transition-colors mt-2 text-sm"
                  >
                    {isPromptExpanded ? "Show less" : "Show more"}
                  </button>
                </div>
              </div>

              {/* Effect Info */}
              {content.effect && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <Star className="w-5 h-5" />
                    <span>Effect Used</span>
                  </h3>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">
                        {content.effect.title}
                      </p>
                      <p className="text-sm text-gray-400">
                        {content.effect.description}
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    onClick={() =>
                      requireAuth(() => setSelectedEffect(content.effect))
                    }
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <span>Use Effect</span>
                    </motion.button>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                  <Tag className="w-5 h-5" />
                  <span>Tags</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {content.tags.map((tag) => (
                    <Link to={`/tags/${tag}`} key={tag}>
                      <span className="bg-gray-800/50 text-gray-300 px-3 py-1 rounded-full text-sm border border-gray-700/50 hover:border-purple-500/50 transition-colors cursor-pointer">
                        #{tag}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-700/50">
                  <span className="text-gray-400 text-sm">Category</span>
                  <span className="text-white text-sm font-medium">
                    {content.category}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-700/50">
                  <span className="text-gray-400 text-sm">Type</span>
                  <span className="text-white text-sm font-medium capitalize">
                    {content.type}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-400 text-sm">Created</span>
                  <span className="text-white text-sm font-medium">
                    {new Date(
                      (content.createdAt as any).seconds * 1000
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              </div>
              {/* Comments Section */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Comments ({comments.length})
                </h3>

                {/* Comment Input */}
                <form
                  onSubmit={handleCommentSubmit}
                  className="flex items-start space-x-3 mb-6"
                >
                  <img
                    src={
                      userGlobalData?.userData?.avatarUrl ||
                      "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                    }
                    alt="Your avatar"
                    className="w-9 h-9 rounded-full"
                  />
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full bg-gray-800/50 rounded-lg p-3 text-sm text-gray-300 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:outline-none transition"
                      rows={2}
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim() || isLoading}
                      className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {isLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
                {/* Comment List */}
                <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                  {comments.map((comment, index) => (
                    <div
                      key={comment.id}
                      ref={
                        index === comments.length - 1
                          ? lastCommentElementRef
                          : null
                      }
                      className="flex items-start space-x-3"
                    >
                      <img
                        src={
                          // comment.creator.avatarUrl ??
                          "https://media.istockphoto.com/id/1925148297/fr/photo/sci-fi-landscape-with-glowing-neon-sphere.webp?a=1&b=1&s=612x612&w=0&k=20&c=NLt8FX7bc5JzWmbVXXL8pvpKmAeDG9uNcefMRydu_7E="
                        }
                        alt={comment.creator.username}
                        className="w-9 h-9 rounded-full"
                      />
                      <div className="flex-1 bg-gray-800/50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-white text-sm">
                            {comment.creator.username}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(
                              (comment.createdAt as any).seconds * 1000
                            ).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-sm text-gray-300 mt-1">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="text-center py-4">
                      <p>Loading more comments...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <Transition appear show={showSuccessModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={() => setShowSuccessModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-white flex items-center"
                  >
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    Comment Submitted!
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-400">
                      Your comment has been successfully submitted.
                    </p>
                  </div>

                  <div className="mt-4">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                      onClick={() => setShowSuccessModal(false)}
                    >
                      Got it, thanks!
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <AddToAlbumModal
        contentId={contentId}
        isOpen={showAddToAlbumModal}
        onClose={() => setShowAddToAlbumModal(false)}
      />
      {showPublishModal && (
        <PublishContent
          item={content as LibraryItem}
          onClose={() => setShowPublishModal(false)}
        />
      )}
      <EffectDescriptionPanel
        effect={selectedEffect}
        onClose={() => setSelectedEffect(null)}
        onStartEditing={handleStartEditing}
        preventExitFullscreen={true}
      />
      <LoginPromptModal
        isOpen={isLoginPromptModalOpen}
        onClose={() => setIsLoginPromptModalOpen(false)}
      />
      <CreationReadyModal
        isOpen={isCreationReadyModalOpen}
        onClose={() => setIsCreationReadyModalOpen(false)}
        onNavigate={() => {
          if (newCreationId) {
            navigate(`/interactive-editing/${newCreationId}`);
          }
        }}
      />
    </motion.div>
  );
};

export default ContentModal;
